import {
  JSON_RENDER_TOOL_CHAIN_HANDLER,
  toolsFromDispatchParams,
} from '@galapagos/catalog-schema/tool-bindings';
import { DateTime } from 'luxon';
import { applyWeeklyDayDates } from './week-dates';
import {
  binaryWeekValues,
  binaryChoiceValue,
  hasCompatibleUsageCapture,
  isUnknownRecord,
  getJsonPointerValue,
  readAnswer,
  usageProvenance,
  usageCaptureProvenanceSchema,
  type FlowDefinition,
  type FlowScreen,
  type OnboardingUsageProgram,
  type FlowStateAdapter,
  type OnboardingUsageCopy,
} from '@galapagos/shared';
import { calculateWeekSummary, type CalculateWeekSummaryOptions } from './calculate-week-summary';

/** Apply the active locale's presentation copy without changing capture provenance. */
export function withUsageChoiceLabels(
  program: OnboardingUsageProgram | undefined,
  localizedCopy: Partial<OnboardingUsageCopy>,
): OnboardingUsageProgram | undefined {
  const copyKeys = [
    'yes',
    'no',
    'weeklyTitle',
    'weeklySubtitle',
    'typicalTitle',
    'typicalSubtitle',
    'use',
    'noUse',
    'usedDays',
    'noUseDays',
    'unanswered',
    'required',
    'reviewTitle',
    'reviewBody',
    'reviewWeekHeading',
    'reviewTotalLabel',
    'reviewFreeLabel',
  ] as const;
  if (
    !program ||
    program.captureMode !== 'yes-no' ||
    copyKeys.every(
      (key) => localizedCopy[key] === undefined || program.copy[key] === localizedCopy[key],
    )
  )
    return program;
  const copy = { ...program.copy };
  for (const key of copyKeys) {
    const value = localizedCopy[key];
    if (value !== undefined) copy[key] = value;
  }
  return { ...program, copy };
}

export function usageAnchor(answers: Record<string, unknown>): string {
  const stored = readAnswer(answers, 'week_anchor_date');
  if (
    typeof stored === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(stored) &&
    DateTime.fromISO(stored, { zone: 'local' }).isValid
  )
    return stored;
  return DateTime.now().setZone('local').toISODate() ?? '';
}
export function isUsageStep(step: string): boolean {
  return /^(weekly_drinks|typical_drinks|week_summary|summary)(_|$)/.test(step);
}

/** Downstream screens must revalidate the report before allowing completion. */
export function requiresUsageCapture(definition: FlowDefinition, step: string): boolean {
  if (isUsageStep(step)) return true;
  // Invalidated answers can truncate the currently resolved path before a saved
  // downstream screen. Authored reachability still identifies that dependency.
  const pending = definition.navigation
    .filter((rule) => isUsageStep(rule.from_step_key))
    .map((rule) => rule.target_step_key);
  const visited = new Set<string>();
  for (let index = 0; index < pending.length; index++) {
    const current = pending[index];
    if (current === undefined || visited.has(current)) continue;
    if (current === step) return true;
    visited.add(current);
    for (const rule of definition.navigation)
      if (rule.from_step_key === current) pending.push(rule.target_step_key);
  }
  return false;
}

/** Recompose a dated bundled definition without mutating the source or changing navigation. */
export function applyOnboardingUsageConfig(
  definition: FlowDefinition,
  program: OnboardingUsageProgram,
  anchor = usageAnchor({}),
  locale = definition.locale ?? 'en',
): FlowDefinition {
  const dated = applyWeeklyDayDates(
    definition,
    DateTime.fromISO(anchor, { zone: 'local' }).toJSDate(),
  );
  if (program.captureMode !== 'yes-no')
    return program.captureMode === 'counter' ? dated : definition;
  const dates = Array.from({ length: 7 }, (_, i) =>
    DateTime.fromISO(anchor, { zone: 'UTC' })
      .setLocale(locale)
      .minus({ days: i + 1 }),
  );
  const screens = Object.fromEntries(
    Object.entries(dated.screens).map(([key, screen]) => {
      if (!isUsageStep(key)) return [key, screen];
      const result: FlowScreen = structuredClone(screen);
      const elements = result.spec.elements;
      const typical = key.startsWith('typical_drinks');
      const picker = key.startsWith('weekly_drinks') || typical;
      const review = key.startsWith('week_summary');
      for (const [id, element] of Object.entries(elements)) {
        if (!isUnknownRecord(element) || !isUnknownRecord(element.props)) continue;
        const props = element.props;
        if (picker && element.type === 'NumberPicker') {
          const value = props.value;
          if (
            !value ||
            typeof value !== 'object' ||
            !('$bindState' in value) ||
            typeof value.$bindState !== 'string'
          )
            continue;
          const path = value.$bindState.replace(/(typical_)?day_(\d+)$/, '$1binary_day_$2');
          const day = dates[Number(path.at(-1)) - 1];
          if (!day) continue;
          const weekday = day.toFormat('cccc');
          const dateLabel = day.toLocaleString({ month: 'short', day: 'numeric' });
          const choiceContext = typical ? weekday : `${weekday}, ${dateLabel}`;
          elements[id] = {
            type: 'SingleSelect',
            props: {
              label:
                !typical && path.endsWith('binary_day_1') && locale.startsWith('en')
                  ? props.label
                  : weekday,
              labelDescription: typical ? undefined : dateLabel,
              layout: 'inline',
              value: { $bindState: path },
              options: [
                {
                  label: program.copy.no ?? program.copy.noUse,
                  value: '0',
                  accessibilityLabel: `${choiceContext}, ${program.copy.no ?? program.copy.noUse}`,
                },
                {
                  label: program.copy.yes ?? program.copy.use,
                  value: '1',
                  accessibilityLabel: `${choiceContext}, ${program.copy.yes ?? program.copy.use}`,
                },
              ],
              testID: `onboarding-${typical ? 'typical' : 'weekly'}-usage-binary-day-${path.at(-1)}`,
            },
          };
        }
        if (picker && element.type === 'Screen') {
          props.accessibilityTitle = typical ? program.copy.typicalTitle : program.copy.weeklyTitle;
          props.contentPaddingHorizontal = 'm';
          props.footerPaddingHorizontal = 'm';
        }
        if (picker && id === 'heading')
          props.value = typical ? program.copy.typicalTitle : program.copy.weeklyTitle;
        if (picker && id === 'subtitle') {
          props.value = typical ? program.copy.typicalSubtitle : program.copy.weeklySubtitle;
          props.color = 'primary';
        }
        if (picker && id === 'continueButton') props.state = 'Default';
        if (picker && id === 'content' && Array.isArray(element.children)) {
          const children: unknown[] = element.children;
          element.children = [
            'usageHeader',
            ...children.filter(
              (c): c is string =>
                typeof c === 'string' &&
                !['usageHeader', 'heading', 'subtitle', 'unitCallout', 'columnHeaders'].includes(c),
            ),
          ];
        }
        if (review && element.type === 'Screen') {
          props.contentPaddingHorizontal = 'm';
          props.footerPaddingHorizontal = 'm';
          if (program.copy.reviewTitle) props.accessibilityTitle = program.copy.reviewTitle;
        }
        if (review && id === 'heading' && program.copy.reviewTitle)
          props.value = program.copy.reviewTitle;
        if (review && id === 'body') {
          if (program.copy.reviewBody) props.value = program.copy.reviewBody;
          props.color = 'primary';
        }
        if (review && id === 'content')
          element.children = ['reviewHeader', 'metrics', 'reviewWeek'];
        if (review && id === 'metrics') props.gap = 's';
        if (review && id === 'weekGrid') props.gap = 's';
        if (review && id === 'footer') props.gap = 'lg';
        if (review && element.type === 'NumberTile') props.fluid = true;
        if (review && /^day.*Label$/.test(id)) {
          const ordinal = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].findIndex(
            (key) => id === `day${key}Label`,
          );
          if (ordinal >= 0) props.value = dates[ordinal]?.toFormat('ccc');
          props.variant = 'c1Medium';
          props.color = 'primary';
        }
        if (id === 'metricTotal')
          props.label = program.copy.reviewTotalLabel ?? program.copy.usedDays;
        if (id === 'metricFree')
          props.label = program.copy.reviewFreeLabel ?? program.copy.noUseDays;
        if (id === 'legendFree') props.label = program.copy.noUse;
        if (id === 'legendDrinking') props.label = program.copy.use;
        if (element.type === 'NumberTile' && id.endsWith('Tile')) {
          const value = props.value;
          if (isUnknownRecord(value) && typeof value.$bindState === 'string') {
            props.displayValue = {
              $bindState: value.$bindState.replace(/\/value$/, '/displayValue'),
            };
          }
        }
        if (isUnknownRecord(element.on))
          for (const chain of Object.values(element.on)) {
            // Live hosts already validated the parent flow, so its tools sit
            // inside the renderer dispatcher. Previews still supply raw calls.
            const tools =
              isUnknownRecord(chain) && chain.action === JSON_RENDER_TOOL_CHAIN_HANDLER
                ? toolsFromDispatchParams(chain.params)
                : Array.isArray(chain)
                  ? chain
                  : [chain];
            for (const tool of tools) {
              if (
                !isUnknownRecord(tool) ||
                tool.tool_name !== 'calculate_week_summary' ||
                !isUnknownRecord(tool.parameters)
              )
                continue;
              const paths = tool.parameters.sourcePaths;
              if (Array.isArray(paths))
                tool.parameters.sourcePaths = paths.map((p: unknown) =>
                  typeof p === 'string'
                    ? p.replace(/^(\/screen\/)(typical_)?day_(\d+)$/, '$1$2binary_day_$3')
                    : p,
                );
              tool.parameters.dayLabels = dates.map((d) => d.toFormat('cccc'));
              tool.parameters.anchorDate = anchor;
              tool.parameters.totalLabel = program.copy.usedDays;
              tool.parameters.freeLabel = program.copy.noUseDays;
            }
          }
      }
      // Remove unreachable quantity guidance rather than leave it in the document.
      for (const id of Object.keys(elements))
        if (/^unit|^columnHeader|^lastWeekHeader|^typicalWeekHeader/.test(id)) delete elements[id];
      if (picker) {
        // Keep fresh rows neutral; resumed choices win in mergeState.
        // Continue assumes No for untouched days in calculateBinaryUsageSummary.
        result.initial_state = { screen: {} };
        result.validation = undefined;
      }
      if (review) {
        elements.reviewHeader = {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's', paddingHorizontal: 's' },
          children: ['heading', 'body'],
        };
        const reviewItems: Record<string, unknown>[] = [];
        const grid = elements.weekGrid;
        if (isUnknownRecord(grid) && Array.isArray(grid.children)) {
          for (const key of grid.children) {
            if (typeof key !== 'string') continue;
            const cell = elements[key];
            if (!isUnknownRecord(cell) || !Array.isArray(cell.children)) continue;
            const label = elements[String(cell.children[0])];
            const tile = elements[String(cell.children[1])];
            if (
              !isUnknownRecord(label) ||
              !isUnknownRecord(label.props) ||
              !isUnknownRecord(tile) ||
              !isUnknownRecord(tile.props)
            )
              continue;
            reviewItems.push({
              weekdayLabel: label.props.value,
              kind: 'value',
              value: tile.props.displayValue,
              backgroundColor: tile.props.backgroundColor,
              accessibilityLabel: tile.props.accessibilityLabel,
            });
          }
        }
        elements.reviewWeek = {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: [...(program.copy.reviewWeekHeading ? ['reviewWeekHeader'] : []), 'reviewDays'],
        };
        if (program.copy.reviewWeekHeading) {
          elements.reviewWeekHeader = {
            type: 'Stack',
            props: { padding: 's' },
            children: ['reviewWeekHeading'],
          };
          elements.reviewWeekHeading = {
            type: 'Text',
            props: {
              variant: 'b1Bold',
              color: 'primary',
              value: program.copy.reviewWeekHeading,
              testID: 'onboarding-week-summary-week-heading',
            },
          };
        }
        elements.reviewDays = {
          type: 'DaySummary',
          props: {
            labelPlacement: 'above',
            items: reviewItems,
            testID: 'onboarding-week-summary-days',
          },
        };
      }
      if (picker)
        elements.usageHeader = {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's', paddingHorizontal: 's' },
          children: ['heading', 'subtitle'],
        };
      return [key, result];
    }),
  );
  return { ...definition, screens };
}

export class BinaryUsageChoiceError extends Error {}

/** Assume No at Continue and retain the initial defaulted ordinals for audit. */
export function calculateBinaryUsageSummary(
  adapter: FlowStateAdapter,
  program: OnboardingUsageProgram,
  paths: string[],
  options: CalculateWeekSummaryOptions,
  anchor: string,
): { summary: ReturnType<typeof calculateWeekSummary>; updates: Record<string, unknown> } {
  const values = paths.map((path) => {
    const raw = getJsonPointerValue(adapter.answers, path);
    return raw === undefined || raw === null ? 0 : binaryChoiceValue(raw);
  });
  if (values.some((v) => v === undefined)) throw new BinaryUsageChoiceError(program.copy.required);
  const previous = usageCaptureProvenanceSchema.safeParse(
    readAnswer(adapter.answers, 'usage_capture'),
  );
  const initialDefaults =
    previous.success && hasCompatibleUsageCapture(adapter.answers, program)
      ? (previous.data.initialDefaultedNoDays ?? {})
      : {};
  const window = paths[0]?.includes('typical_binary_day_') ? 'typical' : 'reported';
  const defaulted = paths.flatMap((path, index) =>
    getJsonPointerValue(adapter.answers, path) == null ? [index + 1] : [],
  );
  // Older submitted captures have no audit field. Their normalized 0 values
  // cannot prove deliberate No, so leave that week's original defaults unknown.
  const previouslySubmitted =
    previous.success &&
    hasCompatibleUsageCapture(adapter.answers, program) &&
    paths.every(
      (path) =>
        typeof getJsonPointerValue(adapter.answers, path.replace('binary_day_', 'day_')) ===
        'number',
    );
  const capturedDefaults = initialDefaults[window] ?? (previouslySubmitted ? undefined : defaulted);
  const defaultAudit = {
    ...initialDefaults,
    ...(capturedDefaults === undefined ? {} : { [window]: capturedDefaults }),
  };
  const summary = calculateWeekSummary(values, options);
  summary.days = summary.days.map((day, i) => ({
    ...day,
    displayValue: day.isFree
      ? (program.copy.no ?? program.copy.noUse)
      : (program.copy.yes ?? program.copy.use),
    accessibilityLabel: `${options.dayLabels[i]}: ${day.isFree ? program.copy.noUse : program.copy.use}`,
  }));
  const dayAnswers: Record<string, unknown> = Object.fromEntries(
    paths.map((path, i) => [path.replace('binary_day_', 'day_'), values[i]]),
  );
  return {
    summary,
    updates: {
      ...dayAnswers,
      ...Object.fromEntries(paths.map((path, i) => [path, String(values[i])])),
      '/screen/week_anchor_date': anchor,
      '/screen/usage_capture': {
        ...usageProvenance(program, anchor),
        ...(Object.keys(defaultAudit).length ? { initialDefaultedNoDays: defaultAudit } : {}),
      },
    },
  };
}

/** Explicitly invalidate active answers when capture meaning changes; retain the original report. */
export function reconcileUsageCapture(
  answers: Record<string, unknown>,
  program: OnboardingUsageProgram,
  stepKey?: string,
): Record<string, unknown> | null {
  if (program.captureMode === null || hasCompatibleUsageCapture(answers, program)) return null;
  const anchor = usageAnchor(answers);
  // Pin fresh counter drafts before controls render, preserving their entered
  // counts and legacy capture semantics when the local date changes.
  if (program.captureMode === 'counter' && readAnswer(answers, 'usage_capture') == null)
    return !stepKey?.startsWith('weekly_drinks') ||
      readAnswer(answers, 'week_anchor_date') === anchor
      ? null
      : { '/screen/week_anchor_date': anchor };
  const updates: Record<string, unknown> = {
    '/screen/usage_capture': usageProvenance(program, anchor),
    '/screen/week_anchor_date': anchor,
  };
  const previous: Record<string, unknown> = {};
  for (const prefix of ['', 'typical_'])
    for (let i = 1; i <= 7; i++) {
      const key = `${prefix}day_${i}`;
      const v = readAnswer(answers, key);
      if (v !== undefined && v !== null) previous[key] = v;
      updates[`/screen/${key}`] = null;
      const binaryKey = `${prefix}binary_day_${i}`;
      const binaryValue = readAnswer(answers, binaryKey);
      // Clear only a stale saved choice. Missing choices remain unselected
      // until the summary handler materializes No when the member continues.
      if (binaryValue !== undefined && binaryValue !== null) {
        updates[`/screen/${binaryKey}`] = null;
      }
    }
  for (const key of ['baseline_week_summary', 'typical_week_summary', 'week_typical'])
    updates[`/screen/${key}`] = null;
  if (Object.keys(previous).length) {
    const history = readAnswer(answers, 'usage_capture_previous');
    const retainedHistory: unknown[] = Array.isArray(history) ? history : [];
    updates['/screen/usage_capture_previous'] = [
      ...retainedHistory,
      { ...previous, provenance: readAnswer(answers, 'usage_capture') ?? null },
    ];
  }
  return updates;
}

/** A saved selection is not a submitted report until its numeric and summary writes agree. */
function hasCommittedBinaryWeek(answers: Record<string, unknown>, typical = false): boolean {
  const values = binaryWeekValues(answers, typical);
  const summary = readAnswer(answers, typical ? 'typical_week_summary' : 'baseline_week_summary');
  if (!isUnknownRecord(summary) || !Array.isArray(summary.values)) return false;
  const recordedValues: unknown[] = summary.values;
  const usedDays = values.filter((value) => value === 1).length;
  return (
    recordedValues.length === 7 &&
    summary.total === usedDays &&
    summary.freeDays === 7 - usedDays &&
    values.every(
      (value, index) =>
        value !== undefined &&
        readAnswer(answers, `${typical ? 'typical_' : ''}day_${index + 1}`) === value &&
        recordedValues[index] === value,
    )
  );
}

/** A deep link cannot turn an unanswered report into a seeded summary. */
export function usageRecapturePrefix(
  answers: Record<string, unknown>,
  program: OnboardingUsageProgram,
  step: string,
  definition?: FlowDefinition,
): string | null {
  const downstream =
    definition !== undefined && !isUsageStep(step) && requiresUsageCapture(definition, step);
  if (program.captureMode !== 'yes-no' || (!isUsageStep(step) && !downstream)) return null;
  if (!step.startsWith('weekly_drinks') && !hasCommittedBinaryWeek(answers)) return 'weekly_drinks';
  if (downstream || /^summary(_|$)/.test(step)) {
    const typical = readAnswer(answers, 'week_typical');
    if (typical !== 'yes' && typical !== 'no') return 'week_summary';
    if (typical === 'no' && !hasCommittedBinaryWeek(answers, true)) return 'typical_drinks';
  }
  return null;
}
